import { KeepAwake } from '@capacitor-community/keep-awake';
import { storage } from 'boot/firebase';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { isFunction, omit } from 'lodash';
import { Platform } from 'quasar';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { network as useNetworkStore } from 'src/store/network';

const base64ToArrayBuffer = (base64) => {
  const base64Data = base64.replace(/^data:[^;]*;base64,/, '');

  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};
export default {

  addToQueue(payload) {
    this.uploadQueue.push(payload);
  }, // addToQueue()

  removeFromQueue(payload) {
    const index = this.uploadQueue.indexOf(payload);
    if (index !== -1) this.uploadQueue.splice(index, 1);
  }, // removeFromQueue()

  updateProgress(payload) {
    const { queueInfo } = payload;
    const file = this.uploadQueue.find((e) => e.destinationPath === queueInfo.destinationPath
      && e.destinationFilename === queueInfo.destinationFilename);

    file.bytesTransferred = payload.bytesTransferred;
    file.totalBytes = payload.totalBytes;
  }, // updateProgress()

  /**
   * Add a file upload to queue and start uploading it to Firebase Storage
   * @param {Object} fileInfo
   * FileInfo params:
   * @param {string} fileInfo.destinationPath
   * @param {string} fileInfo.destinationFilename
   * @param {Function} fileInfo.callback
   * @param {File} fileInfo.file
   * @param {boolean} fileInfo.isBase64
   * @param {Object} fileInfo.metadata
   * @param {boolean} [fileInfo.offline] should be set to save file info into IDB to be uploaded syncing to online
   * @param {Function} [fileInfo.stateChange]
   * @param {Function} [fileInfo.error]
   * @returns {Promise}
   */
  addToUploadQueue(fileInfo) {
    const network = useNetworkStore();

    if (network.offlineMode && fileInfo.offline) {
      const callback = (typeof fileInfo.callback === 'function') ? fileInfo.callback : null;
      const infoToSave = omit(fileInfo, ['callback', 'offline']);

      const fileUploadingDb = new GenericIndexedDb('filesUploading');
      return fileUploadingDb
        .create(infoToSave, null, false, null, null, true)
        .then((savedInfo) => {
          if (callback) {
            callback(savedInfo);
          }
        });
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const queueInfo = {
          destinationPath: fileInfo.destinationPath,
          destinationFilename: fileInfo.destinationFilename,
          bytesTransferred: 0,
          totalBytes: 1,
        };
        this.addToQueue(queueInfo);

        const metadata = fileInfo.metadata || {};

        window.onbeforeunload = () => (this.uploadQueue.length > 0 ? true : null);

        const storageRef = ref(storage, `${fileInfo.destinationPath}/${fileInfo.destinationFilename}`);

        let fileData = fileInfo.file;

        if (fileInfo.isBase64) {
          fileData = await base64ToArrayBuffer(fileInfo.file);
        }

        const uploadTask = uploadBytesResumable(storageRef, fileData, metadata);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            if (typeof fileInfo.stateChange === 'function') {
              fileInfo.stateChange(snapshot, uploadTask);
            }
            this.updateProgress({
              queueInfo,
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
            });

            if (Platform.is.capacitor) {
            // keep screen on to prevent file uploading pause
              KeepAwake.keepAwake();
            }
          },
          (err) => {
            if (typeof fileInfo.error === 'function') {
              fileInfo.error();
            }

            this.removeFromQueue(queueInfo);
            reject(err);
            if (Platform.is.capacitor) {
            // allow to lock the screen
              KeepAwake.allowSleep();
            }
          },
          () => {
            this.removeFromQueue(queueInfo);

            if (isFunction(fileInfo.callback)) {
              resolve(fileInfo.callback(uploadTask.snapshot.ref));
            }

            if (Platform.is.capacitor) {
            // allow to lock the screen
              KeepAwake.allowSleep();
            }
          },
        );
      } catch (error) {
        reject(error);
      }
    });
  },
};
